<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.revenueTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.revenueSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!byToolType.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.from') }}</label>
          <el-date-picker v-model="fromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="fetchData" />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.to') }}</label>
          <el-date-picker v-model="toDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="fetchData" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <template v-else>
      <!-- KPI cards -->
      <div class="row">
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.totalRevenue') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ money(summary.totalRevenue) }}</h5>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.internalRevenue') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ money(summary.internalRevenue) }}</h5>
            <span class="text-xs text-secondary">{{ summary.internalReservationCount }} {{ $t('reports.reservations') }}</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.externalRentRevenue') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ money(summary.externalRentRevenue) }}</h5>
            <span class="text-xs text-secondary">{{ summary.externalRentReservationCount }} {{ $t('reports.reservations') }}</span>
          </div></div>
        </div>
        <div class="col-xl-3 col-sm-6 mb-4">
          <div class="card"><div class="card-body p-3">
            <p class="text-xs text-uppercase text-secondary mb-1">{{ $t('reports.loanExposure') }}</p>
            <h5 class="mb-0 font-weight-bolder">{{ summary.loanedReservationCount }}</h5>
            <span class="text-xs text-secondary">{{ $t('reports.loanedNoRevenue') }}</span>
          </div></div>
        </div>
      </div>

      <!-- Monthly trend -->
      <div class="card mb-4">
        <div class="card-header pb-0"><h6 class="mb-0">{{ $t('reports.monthlyTrend') }}</h6></div>
        <div class="card-body">
          <div v-if="!monthlyTrend.length" class="text-secondary text-sm">{{ $t('reports.noData') }}</div>
          <canvas v-else id="revenue-trend-chart" height="90"></canvas>
        </div>
      </div>

      <!-- Breakdown by tool type -->
      <div class="card">
        <div class="card-header pb-0"><h6 class="mb-0">{{ $t('reports.byToolType') }}</h6></div>
        <div class="card-body">
          <el-table :data="byToolType" stripe>
            <el-table-column prop="toolType" :label="$t('reports.toolType')" min-width="160" />
            <el-table-column :label="$t('reports.internalRevenue')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.internalRevenue) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.externalRentRevenue')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.externalRevenue) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.totalRevenue')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.totalRevenue) }}</template>
            </el-table-column>
            <el-table-column prop="reservationCount" :label="$t('reports.reservations')" align="center" min-width="100" />
          </el-table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "../../services/api";
import Chart from "chart.js/auto";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";

export default {
  name: "RevenueReport",
  components: { MaterialButton },
  data() {
    const today = new Date();
    const from = new Date();
    from.setMonth(today.getMonth() - 6);
    return {
      fromDate: this.toIso(from),
      toDate: this.toIso(today),
      summary: {},
      byToolType: [],
      monthlyTrend: [],
      loading: true,
      trendChart: null,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  beforeUnmount() {
    this.trendChart?.destroy();
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    money(v) {
      return `${new Intl.NumberFormat("is-IS").format(Math.round(v || 0))} kr.`;
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/Revenue", {
          params: { fromDate: this.fromDate, toDate: this.toDate },
        });
        this.summary = response.data;
        this.byToolType = response.data.byToolType || [];
        this.monthlyTrend = response.data.monthlyTrend || [];
        this.loading = false;
        await this.$nextTick();
        this.renderTrend();
      } catch (e) {
        console.error("RevenueReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
        this.loading = false;
      }
    },
    renderTrend() {
      const ctx = document.getElementById("revenue-trend-chart");
      if (!ctx) return;
      if (this.trendChart) this.trendChart.destroy();
      this.trendChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.monthlyTrend.map((m) => m.monthLabel),
          datasets: [
            { label: this.$t("reports.internalRevenue"), data: this.monthlyTrend.map((m) => m.internalRevenue), backgroundColor: "#5e72e4", stack: "r" },
            { label: this.$t("reports.externalRentRevenue"), data: this.monthlyTrend.map((m) => m.externalRevenue), backgroundColor: "#2dce89", stack: "r" },
          ],
        },
        options: {
          responsive: true,
          plugins: { legend: { position: "bottom" } },
          scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
        },
      });
    },
    onExport() {
      const columns = [
        { key: "toolType", label: this.$t("reports.toolType") },
        { key: "internalRevenue", label: this.$t("reports.internalRevenue") },
        { key: "externalRevenue", label: this.$t("reports.externalRentRevenue") },
        { key: "totalRevenue", label: this.$t("reports.totalRevenue") },
        { key: "reservationCount", label: this.$t("reports.reservations") },
      ];
      exportCsv(`revenue-${this.fromDate}_${this.toDate}`, columns, this.byToolType);
    },
  },
};
</script>
